import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { MatCard, MatCardActions } from '@angular/material/card';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatDatepickerModule, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatNativeDateModule, provideNativeDateAdapter } from '@angular/material/core';
import { MatButton } from '@angular/material/button';
import { MatRadioModule, MatRadioGroup } from '@angular/material/radio';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, Subject, take, takeUntil, tap } from 'rxjs';
import { FormInlineErrorComponent } from '@k12/forms';
import { SubTask, Task, TaskTypeAction, WithdrawalReason } from '@k12/models';
import { SchoolsService } from '@k12/school-services';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';

interface HouseholdTransferWithdrawalFormData {
  studentName?: string | null;
  studentPid?: string | null;
  termName?: string | null;
  termId?: string | null;
  currentSchoolName?: string | null;
  reasonForWithdrawal?: number | null;
  transferSchoolId?: string | null;
  firstDateOfAttendanceNewSchool?: Date | null;
  studentEnrollmentId?: string | null;
}

type TermWindow = {
  schoolTermId: string;
  name: string;
  startDate: Date;
  endDate: Date;
};

interface WithdrawalReasonViewModel extends WithdrawalReason {
  description: string;
}

@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-household-transfer-withdrawal-form',
  imports: [
    ReactiveFormsModule,
    MatInput,
    MatCard,
    MatButton,
    MatCardActions,
    MatFormField,
    MatSuffix,
    MatSelect,
    MatOption,
    MatDatepickerModule,
    MatDatepickerToggle,
    MatNativeDateModule,
    MatRadioModule,
    MatRadioGroup,
    MatProgressSpinnerModule,
    FormInlineErrorComponent,
    NgxMatSelectSearchModule
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './household-transfer-withdrawal-form.component.html',
  styleUrl: './household-transfer-withdrawal-form.component.scss'
})
export class HouseholdTransferWithdrawalFormComponent implements OnInit, OnDestroy {
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  private readonly schoolsService = inject(SchoolsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);

  @Input() task: Task | null;
  @Input() taskTypeAction: TaskTypeAction | null;
  @Input() taskAction: SubTask | null;
  @Input() askIfCurrentlyAttending: boolean = false;
  @Input() isSubmitting = false;
  @Output() submitFormData: EventEmitter<string> = new EventEmitter<string>();

  protected viewMode: 'readonly' | 'edit' = 'edit';
  protected form: FormGroup;

  protected termWindow: TermWindow | null = null;
  protected reasons: WithdrawalReasonViewModel[] = [];
  protected minAttendanceDate: Date | null = null;
  protected maxAttendanceDate: Date | null = null;
  protected context: HouseholdTransferWithdrawalFormData = {};
  protected allTransferSchools: Array<{ schoolId: string | null; name: string }> = [];
  protected filteredTransferSchools: Array<{ schoolId: string | null; name: string }> = [];
  protected transferSchoolFilterCtrl = new FormControl('');
  protected isLoadingStudentInfo = false;
  private transferSchoolSearchText = '';
  private readonly destroy$ = new Subject<void>();

  public ngOnInit(): void {
    this.assertRequiredInputs();
    this.initializeForm();
    this.lockReadOnlyFields();
    this.registerSubscriptions();
    this.restoreFormDataIfPresent();
    this.loadStudentDetails();
    this.getWithdrawalReasons();
    this.loadTransferSchools();
    this.applyReadOnlyStatus();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private assertRequiredInputs(): void {
    if (!this.task || !this.taskTypeAction || !this.taskAction) {
      throw new Error('Task, TaskTypeAction, and TaskAction are required inputs');
    }

    if (!this.taskAction.id) {
      throw new Error('TaskAction must have an id');
    }
  }

  private initializeForm(): void {
    this.form = this.formBuilder.group(
      {
        studentName: null,
        studentPid: null,
        termName: null,
        termId: [null],
        isStudentStillAttending: [null],
        reasonForWithdrawal: [null, Validators.required],
        transferSchoolId: [null],
        firstDateOfAttendanceNewSchool: [null],
        studentEnrollmentId: [null],
      },
      {
        validators: []
      }
    );
  }

  private lockReadOnlyFields(): void {
    this.form.get('studentName')?.disable();
    this.form.get('studentPid')?.disable();
    this.form.get('termName')?.disable();
    this.form.get('termId')?.disable();
  }

  private registerSubscriptions(): void {
    this.form.get('reasonForWithdrawal')?.valueChanges.subscribe((reasonId) => {
      this.applyReasonRules(reasonId as number | null);
    });

    // Subscribe to student still attending changes
    this.form.get('isStudentStillAttending')?.valueChanges.subscribe((isAttending) => {
      this.applyAttendanceRules(isAttending as boolean | null);
    });

    // Subscribe to school search changes
    this.transferSchoolFilterCtrl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((value) => {
        const searchText = (value ?? '').toString();
        
        // Only reload from server if search text is significantly different
        if (searchText.length === 0 || (searchText.length >= 2 && searchText !== this.transferSchoolSearchText)) {
          this.transferSchoolSearchText = searchText;
          this.loadTransferSchools();
        } else {
          // Just apply client-side filter for minor changes
          this.applyTransferSchoolFilter();
          this.cdr.markForCheck();
        }
      });
  }

  private loadStudentDetails(): void {
    const studentEnrollmentId = this.form.get('studentEnrollmentId')?.value;
    
    if (!studentEnrollmentId) return;

    this.isLoadingStudentInfo = true;
    this.cdr.markForCheck();

    this.schoolsService
      .getStudentRoster(studentEnrollmentId)
      .pipe(
        take(1),
        tap((response) => {
          if (response?.entity) {
            // Filter by studentEnrollmentId if provided, otherwise use first item
            const roster = response.entity;

            if (roster) {
              this.form.patchValue(
                {
                  studentName: `${roster.studentFirstName} ${roster.studentLastName}`,
                  studentPid: roster.pidNumber,
                  termName: roster.termName || 'N/A',
                  termId: roster.termId || null
                },
                { emitEvent: false }
              );

              this.context.currentSchoolName = roster.schoolName;

              // Set up term window from roster data if term dates are available
              if (roster.termStartDate && roster.termEndDate) {
                const startDate = new Date(roster.termStartDate);
                const endDate = new Date(roster.termEndDate);
                
                if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())) {
                  startDate.setHours(0, 0, 0, 0);
                  endDate.setHours(0, 0, 0, 0);

                  this.termWindow = {
                    schoolTermId: roster.termId || '',
                    name: roster.termName || '',
                    startDate,
                    endDate
                  };
                  
                  this.minAttendanceDate = startDate;
                  this.maxAttendanceDate = endDate;
                }
              }

              this.isLoadingStudentInfo = false;
              this.cdr.markForCheck();
            }
          }
        }),
        tap(() => {
          // Ensure spinner is hidden even if no data is returned
          this.isLoadingStudentInfo = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }

  private restoreFormDataIfPresent(): void {
    const formData = this.taskAction?.formData;
    if (!formData) return;

    const parsed = this.safeParseJson(formData) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return;

    this.context = this.normalizeKeys(parsed) as HouseholdTransferWithdrawalFormData;
    this.form.patchValue(this.context, { emitEvent: false });

    // Back-compat: older payloads may store the full object.
    const restored = this.form.get('reasonForWithdrawal')?.value as unknown;
    const restoredReasonId = this.normalizeWithdrawalReasonId(restored);

    if (restoredReasonId) {
      // Keep the restored value as-is since it may be the full object or just the ID
      // The normalizeWithdrawalReasonId handles both cases
    }

    this.applyReasonRules(restoredReasonId, false);
  }

  private normalizeKeys(raw: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(Object.entries(raw).map(([k, v]) => [k.charAt(0).toLowerCase() + k.slice(1), v]));
  }

  private applyReadOnlyStatus(): void {
    const statusId = this.taskAction?.taskStatusId;
    if (statusId !== 1 && statusId !== 4) {
      this.form.disable();
    }
  }

  private safeParseJson(value: string): unknown {
    try {
      return JSON.parse(value) as unknown;
    } catch {
      return null;
    }
  }

  private normalizeWithdrawalReasonId(value: unknown): number | null {
    if (typeof value === 'number') {
      return value;
    }

    if (value && typeof value === 'object' && 'withdrawalReasonId' in value) {
      const id = (value as { withdrawalReasonId?: unknown }).withdrawalReasonId;
      if (typeof id === 'number') {
        return id;
      }
    }

    return null;
  }

  protected submitForm(): void {
    // replace OTHER with null before submission since the backend expects null for non-transfer reasons
    const formValue = { ...this.form.getRawValue() };
    if (formValue.transferSchoolId === 'OTHER') {
      formValue.transferSchoolId = null;
    }
    this.submitFormData.emit(JSON.stringify(formValue));
  }

  protected isFormReady(): boolean {
    // Don't allow submission if student is still attending
    const isStillAttending = this.form.get('isStudentStillAttending')?.value;
    if (isStillAttending === true) {
      return false;
    }
    return this.form.valid && this.form.dirty;
  }

  public onBack(): void {
    this.router.navigate(['/']).then();
  }

  protected shouldRequestNewSchool(): boolean {
    const reasonId = this.form.get('reasonForWithdrawal')?.value as number | null;
    // Transfer = 4
    return reasonId === 4;
  }

  protected shouldDisplayCurrentlyAttendingQuestion(): boolean {
    return this.askIfCurrentlyAttending;
  }

  protected shouldShowWithdrawalForm(): boolean {
    // Only show withdrawal form if student is NOT still attending
    const isStillAttending = this.form.get('isStudentStillAttending')?.value;
    // Show form if they answered "No" or if the question isn't being asked
    return !this.askIfCurrentlyAttending || isStillAttending === false;
  }

  protected isStudentStillAttending(): boolean {
    return this.form.get('isStudentStillAttending')?.value === true;
  }

  private applyAttendanceRules(isStillAttending: boolean | null): void {
    const reasonControl = this.form.get('reasonForWithdrawal');
    
    if (isStillAttending === false) {
      // Student is no longer attending, require withdrawal reason
      reasonControl?.setValidators([Validators.required]);
    } else {
      // Student is still attending or no answer yet, don't require reason
      reasonControl?.clearValidators();
    }
    
    reasonControl?.updateValueAndValidity({ emitEvent: false });
    this.cdr.markForCheck();
  }

  private applyReasonRules(reasonId: number | null, emitEvent = true): void {
    const transferSchoolControl = this.form.get('transferSchoolId');
    const firstDateControl = this.form.get('firstDateOfAttendanceNewSchool');

    // Transfer = 4
    if (reasonId === 4) {
      // Use custom validator that allows null (for "Other" option)
      transferSchoolControl?.setValidators([Validators.required]);
      firstDateControl?.setValidators([Validators.required, this.dateRangeValidator.bind(this)]);
    } else {
      transferSchoolControl?.clearValidators();
      transferSchoolControl?.setValue(null, { emitEvent: false });
      firstDateControl?.clearValidators();
      firstDateControl?.setValue(null, { emitEvent: false });
    }

    transferSchoolControl?.updateValueAndValidity({ emitEvent });
    firstDateControl?.updateValueAndValidity({ emitEvent });
    this.form.updateValueAndValidity({ emitEvent: false });
    this.cdr.markForCheck();
  }
  
  private dateRangeValidator(control: AbstractControl): ValidationErrors | null {
    // If no term window (no student/program partner), skip range validation
    if (!control.value || !this.termWindow) {
      return null;
    }

    const selectedDate = new Date(control.value);
    selectedDate.setHours(0, 0, 0, 0);
    const min = this.termWindow.startDate.getTime();
    const max = this.termWindow.endDate.getTime();
    const selected = selectedDate.getTime();

    if (selected < min || selected > max) {
      const dateRange = this.formatDateRange(this.termWindow.startDate, this.termWindow.endDate);
      return {
        dateOutOfRange: {
          message: `Date must be within term range: ${dateRange}`,
          min: this.minAttendanceDate,
          max: this.maxAttendanceDate,
          dateRange
        }
      };
    }

    return null;
  }

  private getWithdrawalReasons(): void {
    this.schoolsService
      .getWithdrawalReasonsForHousehold()
      .pipe(
        take(1),
        tap((resp) => {
          // Need to construct options list from response
          this.reasons = [
            { ...resp?.entity.find((r) => r.withdrawalReasonId === 4), description: 'The student is transferring to another eligible private school' } as WithdrawalReasonViewModel,
            { ...resp?.entity.find((r) => r.withdrawalReasonId === 2), description: 'The student is withdrawing from the K12 Program' } as WithdrawalReasonViewModel,
            { ...resp?.entity.find((r) => r.withdrawalReasonId === 1), description: 'The student never attended this school' } as WithdrawalReasonViewModel
          ]
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }

  private loadTransferSchools(): void {
    this.schoolsService
      .getSchoolsForDropdown(this.transferSchoolSearchText, true)
      .pipe(
        take(1),
        tap((response) => {
          const schools = (response?.entity ?? []).map((school) => ({
            schoolId: school.id,
            name: school.name
          }));
          
          this.allTransferSchools = [...schools, { schoolId: 'OTHER', name: 'Other' }];
          
          // Apply client-side filtering to ensure "Other" always appears
          this.applyTransferSchoolFilter();
          
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }

  private applyTransferSchoolFilter(): void {
    const searchText = this.transferSchoolFilterCtrl.value?.toLowerCase() || '';
    
    if (!searchText) {
      this.filteredTransferSchools = [...this.allTransferSchools];
    } else {
      // Filter schools by search text but always include "Other"
      this.filteredTransferSchools = [
        ...this.allTransferSchools.filter(school => 
          school.schoolId !== 'OTHER' && school.name.toLowerCase().includes(searchText)
        ),
        { schoolId: 'OTHER', name: 'Other' }
      ];
    }
  }

  private formatDateRange(startDate: Date, endDate: Date): string {
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    const start = startDate.toLocaleDateString('en-US', options);
    const end = endDate.toLocaleDateString('en-US', options);
    return `${start} - ${end}`;
  }
}
